import React from 'react';
import { 
  Box, 
  VStack, 
  HStack, 
  Text, 
  Link as ChakraLink, 
  Container, 
  AspectRatio, 
  Heading, 
  Button, 
  Icon, 
  useColorModeValue,
  Divider,
  Flex,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaFacebook, FaInstagram, FaArrowRight } from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';

const Contact = () => {
  const purpleColor = useColorModeValue('purple.600', 'purple.300');
  const orangeColor = useColorModeValue('orange.500', 'orange.300');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const cardBg = useColorModeValue('white', 'gray.800');
  const sectionBg = useColorModeValue('gray.50', 'gray.900');


  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const slideIn = (direction) => ({
    hidden: { opacity: 0, x: direction === 'left' ? -100 : 100 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8 } }
  });

  return (
    <VStack spacing={0} overflowX="hidden" mt="10px" mb="10px" rounded='10px'>
      {/* Hero Section */}
      <Box 
        w="100%" 
        h={{ base: "40vh", md: "50vh" }} 
        bgGradient="linear(to-r, purple.600, orange.500)"
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top="0"
          left="0"
          w="full"
          h="full"
          bgGradient="linear(to-b, rgba(107, 70, 193, 0.7), rgba(255, 165, 0, 0.5))"
        />
        <VStack
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          textAlign="center"
          spacing={4}
          w="full"
          px={4}
        >
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <VStack spacing={4}>
              <motion.div variants={fadeIn}>
                <Text
                  fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
                  fontWeight="extrabold"
                  color="white"
                  textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                >
                  GET IN TOUCH
                </Text>
              </motion.div>
              <motion.div variants={fadeIn}>
                <Text
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="semibold"
                  color="white"
                  textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                  maxW="800px"
                >
                  We'd love to hear from you! Contact us for more information or to get involved.
                </Text>
              </motion.div>
            </VStack>
          </motion.div>
        </VStack>
      </Box>

      {/* Contact Information Section */}
      <Box w="full" py={20} bg={sectionBg}>
        <Container maxW="1200px">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <VStack spacing={12}>
              <motion.div variants={fadeIn}>
                <Text 
                  fontSize={{ base: "3xl", md: "4xl" }} 
                  fontWeight="bold" 
                  textAlign="center"
                  bgGradient="linear(to-r, purple.600, orange.500)"
                  bgClip="text"
                >
                  Contact Information
                </Text>
              </motion.div>

              <HStack
                spacing={8}
                align="stretch"
                justify="center"
                flexDirection={{ base: "column", md: "row" }}
                flexWrap="wrap"
              >
                {/* Charity Director */}
                <motion.div variants={slideIn('left')}>
                  <Box 
                    bg={cardBg}
                    p={8}
                    borderRadius="xl"
                    boxShadow="lg"
                    w={{ base: "100%", md: "350px" }}
                    h="100%"
                  >
                    <VStack spacing={6} align="center" textAlign="center">
                      <Icon as={FaPhone} boxSize={8} color={purpleColor} />
                      <Text fontSize="xl" fontWeight="bold" color={purpleColor}>
                        Charity Director
                      </Text>
                      <Text fontSize="lg" fontWeight="semibold">
                        Pastor Ed
                      </Text>
                      <VStack spacing={2}>
                        <ChakraLink href="mailto:eddyanning@gmail.com" color={orangeColor} _hover={{ textDecoration: 'underline' }}>
                          eddyanning@gmail.com
                        </ChakraLink>
                      </VStack>
                    </VStack>
                  </Box>
                </motion.div>

                {/* Charity Secretary */}
                <motion.div variants={fadeIn}>
                  <Box 
                    bg={cardBg}
                    p={8}
                    borderRadius="xl"
                    boxShadow="lg"
                    w={{ base: "100%", md: "350px" }}
                    h="100%"
                  >
                    <VStack spacing={6} align="center" textAlign="center">
                      <Icon as={FaEnvelope} boxSize={8} color={orangeColor} />
                      <Text fontSize="xl" fontWeight="bold" color={orangeColor}>
                        Charity Secretary
                      </Text>
                      <Text fontSize="lg" fontWeight="semibold">
                        Boladale B
                      </Text>
                      <VStack spacing={2}>
                        <ChakraLink href="mailto:boladalebusari@gmail.com" color={purpleColor} _hover={{ textDecoration: 'underline' }}>
                          alphayouthuk10@gmail.com
                        </ChakraLink>
                      </VStack>
                    </VStack>
                  </Box>
                </motion.div>

                {/* General Contact */}
                <motion.div variants={slideIn('right')}>
                  <Box 
                    bg={cardBg}
                    p={8}
                    borderRadius="xl"
                    boxShadow="lg"
                    w={{ base: "100%", md: "350px" }}
                    h="100%"
                  >
                    <VStack spacing={6} align="center" textAlign="center">
                      <Icon as={FaMapMarkerAlt} boxSize={8} color={purpleColor} />
                      <Text fontSize="xl" fontWeight="bold" color={purpleColor}>
                        Our Location
                      </Text>
                      <Text fontSize="lg" lineHeight="tall" color={textColor}>
                        Fountain of Life Church International
                      </Text>
                      <Text fontSize="md" color={textColor}>
                        Romford, Essex, UK
                      </Text>
                      <Button
                        as={RouterLink}
                        to="/events"
                        colorScheme="purple"
                        variant="outline"
                        rightIcon={<FaArrowRight />}
                        mt={4}
                      >
                        Upcoming Events
                      </Button>
                    </VStack>
                  </Box>
                </motion.div>
              </HStack>
            </VStack>
          </motion.div>
        </Container>
      </Box>

      {/* Contact Form Section */}
      <Box w="full" py={20} bg={cardBg}>
        <Container maxW="1200px">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <HStack
              spacing={10}
              align="flex-start"
              justify="space-between"
              flexDirection={{ base: "column", md: "row" }}
            >
              <motion.div variants={slideIn('right')} style={{ width: '100%' }}>
                <Box 
                  bg={sectionBg}
                  p={8}
                  borderRadius="xl"
                  boxShadow="lg"
                h="100%"
                >
                  <Heading as="h3" fontSize="2xl" mb={6} color={orangeColor}>
                    Visit Us
                  </Heading>
                  
                  <AspectRatio ratio={16 / 9} mb={6}>
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2479.6608632176126!2d0.16356851300715283!3d51.5744503717111!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a66cb1967fd3%3A0x98e27e1f480af9ed!2sFountain%20of%20life%20church%20Int'l-%20(Uk)!5e0!3m2!1sen!2suk!4v1692554059994!5m2!1sen!2suk"
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Fountain of Life Church Location Map"
                    ></iframe>
                  </AspectRatio>
                  
                  <Text fontSize="lg" mb={4} color={textColor}>
                    <strong>Address:</strong> 248 London Rd, Fountain of Life Church International, Romford, Essex, UK
                  </Text>
                  
                  
                  <Divider my={6} />
                  
                  <Heading as="h4" fontSize="xl" mb={4} color={purpleColor}>
                    Connect With Us
                  </Heading>
                  
                  <Flex justify="center" spacing={4}>
                    <Button
                      as={ChakraLink}
                      href="https://www.facebook.com/ayouthuk"
                      aria-label="Facebook"
                      colorScheme="facebook"
                      leftIcon={<FaFacebook />}
                      mr={2}
                    >
                      Facebook
                    </Button>
                    <Button
                      as={ChakraLink}
                      href="https://www.instagram.com/alphayouthuk/"
                      aria-label="Instagram"
                      colorScheme="pink"
                      leftIcon={<FaInstagram />}
                    >
                      Instagram
                    </Button>
                  </Flex>
                </Box>
              </motion.div>
            </HStack>
          </motion.div>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box w="full" py={20} bgGradient="linear(to-r, purple.600, orange.500)">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
        >
          <VStack spacing={8} maxW="800px" mx="auto" px={4} textAlign="center">
            <Text 
              fontSize={{ base: "3xl", md: "4xl" }} 
              fontWeight="bold" 
              color="white"
            >
              Ready to Get Involved?
            </Text>
            
            <Text 
              fontSize="xl" 
              color="whiteAlpha.900"
            >
              Join our next event or become a volunteer to make a difference in young people's lives.
            </Text>
            
            <HStack spacing={4} pt={4} justify="center" flexWrap="wrap">
              <Button
                as={RouterLink}
                to="/events"
                size="lg"
                colorScheme="whiteAlpha"
                rightIcon={<FaArrowRight />}
                px={8}
                borderRadius="full"
                _hover={{ transform: "scale(1.05)" }}
                transition="all 0.2s"
              >
                View Events
              </Button>
              
              <Button
                as={RouterLink}
                to="/about"
                size="lg"
                variant="outline"
                color="white"
                borderColor="white"
                rightIcon={<FaArrowRight />}
                px={8}
                borderRadius="full"
                _hover={{ bg: "whiteAlpha.200", transform: "scale(1.05)" }}
                transition="all 0.2s"
              >
                Learn More
              </Button>
            </HStack>

            <Text color="whiteAlpha.800" mt={8}>
              Alpha Youth UK is a registered charity in England and Wales (1145081)
            </Text>
          </VStack>
        </motion.div>
      </Box>
    </VStack>
  );
};

export default Contact;